import StandardizedItemSelector from "./StandardizedItemSelector";
import {useEffect, useRef, useState} from "react";
import useInterventionRepository from "../../../../../../repository/emergency/useInterventionRepository";
import {IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField} from "@mui/material";
import Box from "@mui/material/Box";
import QuotationItemOption from "./QuotationItemOption";
import useHelper from "../../../../../../use/useHelper";
import ShiftingAndMainWorkSupplement from "./ShiftingAndMainWorkSupplement";
import isNull from "../../../../../../mixin/isNull";
import ModalConfig from "../../../../../../material/modal/ModalConfig";
import Modal from "../../../../../../material/modal/Modal";
import UpdateQuotationItem from "./UpdateQuotationItem";
import VatSelector from "./VatSelector";
import Typography from "@mui/material/Typography";
import {ArrowUpward, Help} from "@mui/icons-material";
import HelpVat from "./HelpVat";
import HelpCompanyNumber from "./HelpCompanyNumber";
import PopperWrapper from "../../../../../../material/popper/PopperWrapper";
import parameter from "../../../../../../enum/parameter";
import useEventDispatcher from "../../../../../../use/useEventDispatcher";

const DEFAULT_VAT = 10;

export default function ({orderId, onQuotationPayloadChange, onQuotationItemPayloadChange}) {
    const helper = useHelper();
    const eventDispatcher = useEventDispatcher();
    const interventionRepository = useInterventionRepository();
    const [quotationItems, setQuotationItems] = useState([]);
    const [vat, setVat] = useState(DEFAULT_VAT);
    const [companyNumber, setCompanyNumber] = useState(null);
    const [updateQuotationItem, setUpdateQuotationItem] = useState(null);
    const [openVatHelp, setOpenVatHelp] = useState(false);
    const [openCompanyNumberHelp, setOpenCompanyNumberHelp] = useState(false);
    const vatSelectorRef = useRef();
    const quotationNumberRef = useRef();

    useEffect(() => {
        setTimeout(() => eventDispatcher.launcher("change-vat"), 100);
        setTimeout(() => eventDispatcher.launcher("quotation-number"), parameter.POPUP_TIME + 100);
    }, []);

    useEffect(() => {
        onQuotationPayloadChange({
            vat: 10,
            company_number: null
        });
    }, []);

    useEffect(() => {
        onQuotationItemPayloadChange(quotationItems)
    }, [JSON.stringify(quotationItems)]);

    useEffect(() => {
        onQuotationPayloadChange({
            vat: vat,
            company_number: companyNumber
        });
    }, [vat, companyNumber]);

    /**
     * Put included default item (Shifting, Main work, night supplement)
     * They are not removable, updatable etc
     */
    const addFixedItems = async () => {
        const items = await interventionRepository.getSupportedPriceByOrder(orderId);

        const result = items.map(i => {
            return {
                name: i.name,
                description: null,
                price: i.price,
                quantity: 1,
                standardized_item_id: null,
                default_item_id: i.default_item_id
            }
        });

        setQuotationItems(result);
    }

    const toggleVatHelp = o => () => setOpenVatHelp(o);
    const toggleCompanyNumberHelp = o => () => setOpenCompanyNumberHelp(o);

    const handleVatChange = e => setVat(e.target.value);
    const handleAdd = qi => setQuotationItems(prevState => [...prevState, qi]);
    const handleDelete = index => () => setQuotationItems(prevState => [...prevState.filter((ps, i) => i != index)]);
    const handleUpdate = index => quotationItem => {
        setUpdateQuotationItem(null);

        const current = [...quotationItems];

        current[index] = quotationItem;

        setQuotationItems(current);
    }

    const formatName = name => {
        if (isNull(name)) return name;

        return name.substring(0, 1).toUpperCase() + name.substring(1).toLowerCase();
    }

    return (
        <>
            <Table size={"small"}>
                <TableHead>
                    <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Qté</TableCell>
                        <TableCell>HT</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        quotationItems.map((qi, i) => (
                            <TableRow key={i} data-test="quotation-item-row">
                                <TableCell width={"65%"}>
                                    <Typography fontSize={"small"}>
                                        {formatName(qi.name)}
                                    </Typography>
                                </TableCell>
                                <TableCell width={"15%"}>{helper.priceFormatter(qi.quantity)}</TableCell>
                                <TableCell width={"15%"}>{helper.priceFormatter(qi.price)}</TableCell>
                                <TableCell width={"5%"}>
                                    <QuotationItemOption
                                        quotationItem={qi}
                                        onDelete={handleDelete(i)}
                                        onUpdate={() => setUpdateQuotationItem(i)}
                                        disabled={!isNull(qi.default_item_id)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))
                    }
                    {
                        quotationItems.length === 0
                            ? (
                                <TableRow data-test="quotation-item-row">
                                    <TableCell colSpan={4}>
                                        <Typography textAlign="center" variant="caption">
                                            Votre DEP & MO sont déjà inclus dans le devis
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )
                            : null
                    }
                    <TableRow>
                        <TableCell colSpan={2}>
                            <Box display="flex" alignItems="center" ref={vatSelectorRef}>
                                <div>
                                    <IconButton
                                        size="small"
                                        color={"warning"}
                                        onClick={toggleVatHelp(true)}
                                        data-test="button-help-vat"
                                    >
                                        <Help/>
                                    </IconButton>
                                </div>
                                <Typography fontSize={"small"} fontWeight={"bold"}>
                                    TVA
                                </Typography>
                            </Box>
                            <PopperWrapper
                                elementRef={vatSelectorRef}
                                placement={"bottom"}
                                content={"Changez la TVA en fonction de votre entreprise"}
                                triggeringEvent={"change-vat"}
                                autoCloseAfter={parameter.POPUP_TIME}
                                displayCloseButton={false}
                                contentColor={parameter.POPUP_COLOR}
                                icon={<ArrowUpward color={parameter.POPUP_COLOR}/>}
                            />
                        </TableCell>
                        <TableCell colSpan={2}>
                            <VatSelector defaultValue={DEFAULT_VAT} onChange={handleVatChange}/>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={2}>
                            <Box display="flex" alignItems="center" ref={quotationNumberRef}>
                                <div>
                                    <IconButton
                                        size="small"
                                        color={"warning"}
                                        onClick={toggleCompanyNumberHelp(true)}
                                        data-test="button-help-quotation-number"
                                    >
                                        <Help/>
                                    </IconButton>
                                </div>
                                <Typography fontSize={"small"} fontWeight={"bold"}>
                                    N° devis de votre entreprise
                                </Typography>
                            </Box>
                            <PopperWrapper
                                elementRef={quotationNumberRef}
                                placement={"bottom"}
                                content={"Renseignez le numéro de devis de votre entreprise"}
                                triggeringEvent={"quotation-number"}
                                autoCloseAfter={parameter.POPUP_TIME}
                                displayCloseButton={false}
                                contentColor={parameter.POPUP_COLOR}
                                icon={<ArrowUpward color={parameter.POPUP_COLOR}/>}
                            />
                        </TableCell>
                        <TableCell colSpan={2}>
                            <TextField
                                variant={"standard"}
                                fullWidth
                                onChange={e => setCompanyNumber(e.target.value)}
                                data-test="input-quotation-number"
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Box mt={5}>
                <StandardizedItemSelector
                    orderId={orderId}
                    onSelect={handleAdd}
                    currentQuotationItems={quotationItems}
                />
            </Box>
            <Box mt={5}>
                <ShiftingAndMainWorkSupplement
                    orderId={orderId}
                    onAdd={handleAdd}
                    currentQuotationItems={quotationItems}
                />
            </Box>

            {
                !isNull(updateQuotationItem)
                    ? (
                        <Modal
                            modalConfig={
                                new ModalConfig()
                                    .setOnClose(() => setUpdateQuotationItem(null))
                                    .setWidth("100%")
                                    .setHeight("80%")
                                    .setComponent(
                                        <UpdateQuotationItem
                                            quotationItem={quotationItems[updateQuotationItem]}
                                            onSubmit={handleUpdate(updateQuotationItem)}
                                        />
                                    )
                            }
                        />
                    )
                    : null
            }

            <HelpVat open={openVatHelp} onClose={toggleVatHelp(false)}/>
            <HelpCompanyNumber open={openCompanyNumberHelp} onClose={toggleCompanyNumberHelp(false)}/>

        </>
    )
}